import { DeeplyReadonly, Query } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { ErrorOutline } from '@mui/icons-material';
import { Button, Dialog, ListItem } from '@mui/material';
import {
  ExtendedList,
  ExtendedListItem,
  ExtendedListItemText,
  ExtendedListProps,
  formatNumber,
  NumberFormatPreset,
  Widget,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetIcon,
  ExtendedListItemButton,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { UTILIZATION_LIMIT } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { VirtualList } from '~/components/VirtualList/VirtualList';
import { useOpenClose } from '~/hooks';
import { RouteId } from '~/routes';

const MAX_ITEMS = 4;
const formatValue = formatNumber(NumberFormatPreset.PERCENT);

enum AlertType {
  UtilizationExceeded = 'utilizationExceeded',
}

type ItemData = {
  id: string;
  type: AlertType;
  contractId: string;
  utilization: number;
};

export const ContractAlertsWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractAlertsWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'contract-alerts' });
  const [dialogProps, dialogOpen] = useOpenClose();

  const cube = 'ContractUtilization';

  const query: DeeplyReadonly<Query> = {
    dimensions: [`${cube}.sourceReference`, `${cube}.totalContractCount`, `${cube}.contractId`, `${cube}.utilization`],
    order: {
      [`${cube}.utilization`]: 'desc',
    },
    filters: [
      {
        member: `${cube}.utilization`,
        operator: 'gt',
        values: [`${UTILIZATION_LIMIT}`],
      },
      {
        member: `${cube}.supplierId`,
        operator: 'equals',
        values: [entityId],
      },
    ],
  };

  const queryValue = useCubeQuery({ ...query, limit: MAX_ITEMS });

  const contractAlertSegments: ItemData[] | undefined = queryValue.resultSet?.rawData().length
    ? queryValue.resultSet?.rawData()?.map((segment) => ({
        id: segment[`${cube}.sourceReference`],
        type: AlertType.UtilizationExceeded,
        totalCount: segment[`${cube}.totalContractCount`],
        contractId: segment[`${cube}.contractId`],
        utilization: formatValue(Number(segment[`${cube}.utilization`])),
      }))
    : undefined;

  const successItems = [{ id: 'success', title: t('success.title'), description: t('success.description') }];

  const totalCount = queryValue.resultSet?.rawData()?.[0]?.[`${cube}.totalContractCount`] ?? 0;
  const hasMore = totalCount > MAX_ITEMS;
  const isNodesEmpty = totalCount === 0;

  const renderItem: NonNullable<ExtendedListProps<ItemData>['renderItem']> = useMemo(
    () =>
      ({ item }) => {
        const { id, type, contractId, utilization } = item;
        return (
          <ListItem disablePadding>
            <ExtendedListItemButton color="error" component={Link} to={RouteId.contractDetail(contractId)}>
              <ExtendedListItemText
                primary={t('itemTitle', { sourceReference: id })}
                secondary={t(`description.${type}`, { utilization, limit: UTILIZATION_LIMIT * 100 })}
              />
            </ExtendedListItemButton>
          </ListItem>
        );
      },
    [t],
  );

  const renderSuccessItem = ({ item }: { item: { title: string; description: string } }) => {
    const { title, description } = item;
    return (
      <ExtendedListItem color="success">
        <ExtendedListItemText primary={title} secondary={description} />
      </ExtendedListItem>
    );
  };

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="ContractAlertsWidget">
        <WidgetHeader title={t('title')} icon={<WidgetIcon color="error" icon={<ErrorOutline fontSize="small" />} />} />
        <WidgetContent>
          {!isNodesEmpty ? (
            <ExtendedList items={contractAlertSegments} renderItem={renderItem} />
          ) : (
            <ExtendedList items={successItems} renderItem={renderSuccessItem} />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" onClick={dialogOpen} disabled={!hasMore || isNodesEmpty}>
            {t('showAll', { count: totalCount })}
          </Button>
        </WidgetFooter>
      </Widget>
      <Dialog {...dialogProps}>
        <WidgetHeader title={t('title')} icon={<WidgetIcon color="error" icon={<ErrorOutline fontSize="small" />} />} />
        <VirtualList
          itemSize={114}
          query={query}
          queryOptions={{
            segmentMapper: (segment) => ({
              id: segment[`${cube}.sourceReference`],
              type: AlertType.UtilizationExceeded,
              contractId: segment[`${cube}.contractId`],
              utilization: formatValue(segment[`${cube}.utilization`] / 100),
            }),
            skip: !dialogProps.open,
            variables: {
              id: entityId,
              limit: 1000,
            },
          }}
          renderItem={renderItem as any}
        />
      </Dialog>
    </>
  );
};
